import { SelectionUtils } from "@/utils/SelectionUtils";
import { IconLink, IconUnlink } from "@codexteam/icons";
import { API, BlockAPI, InlineTool, SanitizerConfig, ToolConfig } from "@editorjs/editorjs";
import { InlineToolbar } from "@editorjs/editorjs/types/api/inline-toolbar";
import { Toolbar } from "@editorjs/editorjs/types/api/toolbar";
import { createRoot } from "react-dom/client";
import InternalLinkToolDropdown from "./InternalLinkToolDropdown";

export interface ElementInfo {
    id: string;
    name: string;
    slug: string;
}

export interface InternalLinkToolConfig {
    elements: Array<ElementInfo>;
}

export class InternalLinkTool implements InlineTool {
    public static isInline: boolean = true;
    public static title: string = "Internal Link";

    public static get sanitize(): SanitizerConfig {
        return {
            a: {
                href: true,
                id: "internal-link",
                "data-internal-element-id": true,
                "data-internal-element-slug": true,
            },
        } as SanitizerConfig;
    }

    private readonly commandLink: string = "createLink";
    private readonly commandUnlink: string = "unlink";

    private readonly CSS = {
        wrapper: "ids-platform-inline-tool",
        button: "ce-inline-tool",
        buttonActive: "ce-inline-tool--active",
        buttonModifier: "ce-inline-tool--link",
        buttonUnlink: "ce-inline-tool--unlink",
        input: "ce-inline-tool-input",
        inputShowed: "ce-inline-tool-input--showed",
    };

    private nodes: {
        button: HTMLButtonElement | null;
        input: HTMLDivElement | null;
    } = {
        button: null,
        input: null,
    };

    private selection: SelectionUtils;
    private inputOpened = false;
    private toolbar: Toolbar;
    private inlineToolbar: InlineToolbar;
    private config?: InternalLinkToolConfig;
    private block?: BlockAPI;

    public elementSlug?: string;

    constructor({ api, config }: { api: API; config?: ToolConfig<InternalLinkToolConfig> }) {
        this.toolbar = api.toolbar;
        this.config = config;
        this.inlineToolbar = api.inlineToolbar;
        this.selection = new SelectionUtils();

        const currentBlockIndex = api.blocks.getCurrentBlockIndex();
        try {
            this.block = api.blocks.getBlockByIndex(currentBlockIndex);
        } catch {
            this.block = undefined;
        }
    }

    private dispatchChange(): void {
        if (!this.block) return;
        this.block.dispatchChange();
    }

    public render(): HTMLElement {
        this.nodes.button = document.createElement("button") as HTMLButtonElement;
        this.nodes.button.type = "button";
        this.nodes.button.classList.add(this.CSS.button, this.CSS.buttonModifier);

        this.nodes.button.innerHTML = IconLink;

        return this.nodes.button;
    }

    public renderActions(): HTMLElement {
        const rootNode = document.createElement("div");
        rootNode.classList.add(this.CSS.wrapper, this.CSS.input);
        
        this.nodes.input = rootNode;

        const root = createRoot(rootNode);
        root.render(
            <InternalLinkToolDropdown
                config={this.config}
                tool={this}
                onCreateInternalLink={(slug: string) => this.insertLink(slug)}
                onCreateExternalLink={(target: string) => this.insertLink(target, true)}
                onRemove={() => this.removeInternalLink()}
            />
        );

        return this.nodes.input;
    }

    public insertLink(target: string, external: boolean = false) {
        this.selection.restore();
        this.selection.removeFakeBackground();

        if (external) this.createLink(target, "", external);
        else this.createLink("/internal-link", target, external);

        this.selection.collapseToEnd();
        this.inlineToolbar.close();

        this.dispatchChange();
    }

    private createLink(target: string, slug: string, isExternal: boolean = false): void {
        const anchorTag = this.selection.findParentTag("A");

        if (anchorTag) {
            this.selection.expandToTag(anchorTag);
        }

        document.execCommand(this.commandLink, false, "ids-platform-inserted-link");
        const newLink = document.querySelector("a[href='ids-platform-inserted-link']") as HTMLLinkElement;
        if (!newLink) {
            this.unlink();
            return;
        }

        if (isExternal) {
            newLink.href = target;

            return;
        }

        newLink.href = "/internal-link";
        newLink.dataset.internalElementSlug = slug;
    }

    public removeInternalLink() {
        this.selection.restore();
        this.unlink();
        this.closeActions();

        this.dispatchChange();
    }

    public surround(range: Range): void {
        /**
         * Range will be null when user makes second click on the 'link icon' to close opened input
         */
        if (range) {
            /**
             * Save selection before change focus to the input
             */
            if (!this.inputOpened) {
                /** Create blue background instead of selection */
                if (this.selection.savedSelectionRange) {
                    this.selection.removeFakeBackground();
                }
                this.selection.setFakeBackground();
                this.selection.save();
            } else {
                this.selection.restore();
                this.selection.removeFakeBackground();
            }

            const parentAnchor = this.selection.findParentTag("A");
            /**
             * Unlink icon pressed
             */
            if (parentAnchor) {
                this.selection.expandToTag(parentAnchor);
                this.selection.removeFakeBackground();

                this.elementSlug = undefined;
                this.unlink();
                this.closeActions();
                // this.checkState();
                this.toolbar.close();

                return;
            }
        }

        this.toggleActions();
    }

    public checkState(): boolean {
        const anchorTag = this.selection.findParentTag("A") as HTMLAnchorElement;
        if (anchorTag) {
            this.elementSlug = anchorTag.dataset.internalElementSlug;

            if (this.elementSlug) {
                const page = this.config?.elements.find((x) => x.slug === this.elementSlug);
                this.nodes.button!.innerHTML = `${IconUnlink} ${page?.name ?? "Unknown"}`;
            } else {
                this.nodes.button!.innerHTML = `${IconUnlink} ${anchorTag.href}`;
            }

            this.nodes.button!.classList.add(this.CSS.buttonUnlink);
            this.nodes.button!.classList.add(this.CSS.buttonActive);
            this.nodes.button!.style.width = "auto";
            this.nodes.button!.style.padding = "0px 4px";

            return false;
        }

        if (this.nodes.button!.innerHTML !== IconLink) {
            this.nodes.button!.innerHTML = IconLink;
            this.nodes.button!.classList.remove(this.CSS.buttonUnlink);
            this.nodes.button!.classList.remove(this.CSS.buttonActive);
            this.nodes.button!.style.width = "28px";
        }

        return !!anchorTag;
    }

    public clear(): void {
        this.closeActions();
    }

    public get shortcut(): string {
        return "CMD+L";
    }

    private toggleActions(): void {
        if (!this.inputOpened) {
            this.openActions(true);
        } else {
            this.closeActions(false);
        }
    }

    private openActions(needFocus = false): void {
        if (!this.nodes.input) return;

        this.nodes.input.classList.add(this.CSS.inputShowed);

        if (needFocus) {
            const element = document.getElementById("focus-input");
            if (element) element.focus();
        }

        this.inputOpened = true;
    }

    private closeActions(clearSavedSelection = true): void {
        if (!this.nodes.input) return;

        if (this.selection.isFakeBackgroundEnabled) {
            // if actions is broken by other selection We need to save new selection
            const currentSelection = new SelectionUtils();
            currentSelection.save();

            this.selection.restore();
            this.selection.removeFakeBackground();

            // and recover new selection after removing fake background
            currentSelection.restore();
        }

        this.nodes.input.classList.remove(this.CSS.inputShowed);

        if (clearSavedSelection) {
            this.selection.clearSaved();
        }

        this.inputOpened = false;
    }

    private unlink(): void {
        document.execCommand(this.commandUnlink);
    }
}
